import { AlertTriangle, CheckCircle2, CreditCard, HelpCircle, Info, RefreshCw, Undo2, XCircle } from 'lucide-react'
import { useId, useState } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import '../../../shared/ui/aqsi.css'
import { formatKopecks } from '../../../shared/model/format'
import { kopecksInputText, parseKopecks } from '../model/slipRefund'
import type { SlipRefundActions, SlipRefundOutcomeVm, SlipRefundVm, SlipRowVm } from '../model/slipRefundTypes'
import './refunds.css'

/**
 * «Возврат по слип-чеку» (RP-FB74; решение владельца 25.10.2026: возврат по слипу БЕЗ чека нужен). Сначала предупреждение, что это особая
 * форма (подтверждение - осознанное действие человека), потом слипы покупки этой кассы с суммой, временем и остатком «уже возвращено».
 * Человек выбирает слип, вводит сумму (не больше остатка) и подтверждает: нажатие «Вернуть деньги на карту» - ОДНА отправка.
 *
 * Автоматики нет. Кнопки «Повторить» у возврата нет и быть не может: ответ кабинета показывается дословно, а при неизвестном исходе окно
 * говорит «повторять нельзя» и предлагает только «Проверить статус» - чтение, а не отправку.
 */
export function SlipRefundScreen({ vm, actions }: { vm: SlipRefundVm; actions: SlipRefundActions }) {
  const [understood, setUnderstood] = useState(false)
  return (
    <section className="aqsi-screen" aria-labelledby="aqsi-slip-refund-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-slip-refund-title">
          <CreditCard size={18} aria-hidden="true" /> Возврат по слип-чеку
        </h2>
      </header>

      {!understood ? (
        <div className="aqsi-panel" data-tone="warning" role="alertdialog" aria-labelledby="aqsi-slip-refund-warning" aria-describedby="aqsi-slip-refund-warning-text">
          <p id="aqsi-slip-refund-warning" className="aqsi-card__state">
            <AlertTriangle size={16} aria-hidden="true" /> <strong>Особая форма возврата</strong>
          </p>
          <p id="aqsi-slip-refund-warning-text" className="aqsi-card__text">
            Эта форма нужна, если оплата прошла на эквайринговом терминале, а фискальный чек не пробит. Если чек есть, возврат
            делается по чеку: «Стандартный возврат».
          </p>
          <div className="aqsi-card__actions">
            <Button variant="primary" onClick={() => setUnderstood(true)}>
              Понимаю, продолжить
            </Button>
            <Button variant="ghost" icon={<Undo2 size={14} aria-hidden="true" />} onClick={actions.openStandard}>
              Стандартный возврат
            </Button>
            <Button variant="ghost" onClick={actions.close}>
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <Body vm={vm} actions={actions} />
      )}
    </section>
  )
}

function Body({ vm, actions }: { vm: SlipRefundVm; actions: SlipRefundActions }) {
  if (vm.seatMissing) {
    return (
      <div className="aqsi-form">
        <StatusMessage
          kind="empty"
          title="Нет работы за кассой"
          text="Возврат по слипу делает тот, кто работает за кассой: начните работу в РМК, затем вернитесь сюда."
        />
        <div className="aqsi-card__actions">
          <Button variant="ghost" onClick={actions.close}>
            Закрыть
          </Button>
        </div>
      </div>
    )
  }

  if (vm.phase === 'sending') {
    return (
      <div className="aqsi-form">
        <StatusMessage kind="loading" title="Возврат отправлен в кабинет AQSI…" text="Не закрывайте окно и не нажимайте ничего: ответ придёт сюда." />
      </div>
    )
  }

  if (vm.phase === 'result' && vm.outcome !== null) return <Outcome outcome={vm.outcome} statusNote={vm.statusNote} canStartOver={vm.canStartOver} actions={actions} />
  return <Choose vm={vm} actions={actions} />
}

function Outcome({
  outcome,
  statusNote,
  canStartOver,
  actions,
}: {
  outcome: SlipRefundOutcomeVm
  statusNote: string | null
  canStartOver: boolean
  actions: SlipRefundActions
}) {
  const Icon = outcome.tone === 'success' ? CheckCircle2 : outcome.tone === 'failure' ? XCircle : HelpCircle
  const tone = outcome.tone === 'success' ? 'success' : outcome.tone === 'failure' ? 'danger' : 'warning'
  return (
    <div className="aqsi-form" aria-label="Итог возврата по слипу">
      <div className="aqsi-panel" data-tone={tone} role={outcome.tone === 'success' ? 'status' : 'alert'}>
        <p className="aqsi-card__state">
          <Icon size={16} aria-hidden="true" /> <strong>{outcome.title}</strong>
        </p>
        {outcome.facts.length > 0 && (
          <ul className="aqsi-refund__summary">
            {outcome.facts.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        )}
        {outcome.cabinetText !== null && (
          <p className="aqsi-card__text">
            Ответ кабинета AQSI: <span className="aqsi-slip-refund__verbatim">{outcome.cabinetText}</span>
          </p>
        )}
        <p className="aqsi-card__text" role={outcome.mustNotRepeat ? 'note' : undefined}>
          {outcome.note}
        </p>
        {statusNote !== null && (
          <p className="aqsi-card__text" role="status">
            {statusNote}
          </p>
        )}
        {outcome.meta !== null && <p className="aqsi-caption">{outcome.meta}</p>}
      </div>
      <div className="aqsi-card__actions">
        {outcome.mustNotRepeat && (
          <Button variant="primary" icon={<RefreshCw size={14} aria-hidden="true" />} onClick={actions.checkStatus}>
            Проверить статус
          </Button>
        )}
        {canStartOver && (
          <Button variant={outcome.tone === 'success' ? 'primary' : 'secondary'} onClick={actions.startOver}>
            К списку слипов
          </Button>
        )}
        <Button variant="ghost" onClick={actions.close}>
          Закрыть
        </Button>
      </div>
    </div>
  )
}

function Choose({ vm, actions }: { vm: SlipRefundVm; actions: SlipRefundActions }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [amountText, setAmountText] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const confirmId = useId()
  const amountId = useId()
  const status = vm.slipsStatus.status
  const selected = vm.slips.find((slip) => slip.id === selectedId) ?? null
  const amount = parseKopecks(amountText)
  const over = selected !== null && amount !== null && amount > selected.availableKopecks
  const amountError =
    amountText.trim() === '' ? null : amount === null ? 'Сумма: рубли и копейки, например 120 или 120,50' : over ? 'Больше остатка по слипу' : null
  const ready = selected !== null && amount !== null && !over && confirmed && vm.busyReason === null

  const choose = (slip: SlipRowVm) => {
    setSelectedId(slip.id)
    setAmountText(kopecksInputText(slip.availableKopecks))
    setConfirmed(false)
  }

  return (
    <div className="aqsi-form" aria-label="Возврат по слипу">
      <p className="aqsi-card__text">
        Слипы оплаты картой на терминале этой кассы{vm.lookbackDays > 0 ? ` за последние ${vm.lookbackDays} сут.` : ''}: выберите слип, укажите
        сумму возврата - не больше остатка - и подтвердите.
      </p>
      {vm.busyReason !== null && (
        <p className="aqsi-panel" data-tone="warning" role="status">
          <AlertTriangle size={16} aria-hidden="true" /> {vm.busyReason}
        </p>
      )}

      {status === 'idle' || status === 'loading' ? (
        <StatusMessage kind="loading" title="Слипы кассы: загрузка…" />
      ) : status !== 'ready' && status !== 'empty' ? (
        <div role="alert">
          <StatusMessage kind="error" title="Слипы кассы не прочитаны" text={vm.slipsStatus.error?.message ?? 'Кабинет AQSI не ответил'} />
          <div className="aqsi-card__actions">
            <Button variant="secondary" icon={<RefreshCw size={14} aria-hidden="true" />} onClick={actions.reload}>
              Обновить список
            </Button>
          </div>
        </div>
      ) : vm.slips.length === 0 ? (
        <StatusMessage kind="empty" title="Слипов покупки за этот срок нет" text="Если оплата прошла давно, верните деньги на терминале или в кабинете AQSI." />
      ) : (
        <ul className="aqsi-slip-refund__list" aria-label="Слипы кассы">
          {vm.slips.map((slip) => (
            <li key={slip.id} className="aqsi-slip-refund__slip" data-selected={slip.id === selectedId ? '' : undefined}>
              <div>
                <strong>{slip.amountText}</strong> · {slip.timeText}
                <div className="aqsi-caption">
                  Возвращено: {slip.returnedText} · можно вернуть: {slip.availableText}
                </div>
                {slip.hasSale && (
                  <div className="aqsi-caption">
                    <Info size={12} aria-hidden="true" /> Оплата продажи, проведённой в программе: по ней есть чек - обычно возврат делается по чеку.
                  </div>
                )}
              </div>
              <Button
                variant={slip.id === selectedId ? 'primary' : 'secondary'}
                disabled={!slip.canRefund}
                aria-pressed={slip.id === selectedId}
                onClick={() => choose(slip)}
              >
                {slip.canRefund ? (slip.id === selectedId ? 'Выбран' : 'Выбрать') : 'Всё возвращено'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {selected !== null && (
        <div className="aqsi-panel" data-tone="warning">
          <p className="aqsi-card__text">
            <label htmlFor={amountId}>Сумма возврата, ₽</label>{' '}
            <input
              id={amountId}
              inputMode="decimal"
              autoComplete="off"
              value={amountText}
              aria-invalid={amountError !== null}
              onChange={(event) => {
                setAmountText(event.target.value)
                setConfirmed(false)
              }}
            />
          </p>
          {amountError !== null && (
            <p className="aqsi-caption" role="alert">
              <AlertTriangle size={12} aria-hidden="true" /> {amountError}
            </p>
          )}
          <p className="aqsi-caption">Остаток по слипу: {selected.availableText}</p>
          <p className="aqsi-card__text">
            <input id={confirmId} type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />{' '}
            <label htmlFor={confirmId}>
              Подтверждаю: вернуть {amount === null ? '…' : formatKopecks(amount)} на карту по этому слипу. Чек не пробивается, отправка одна.
            </label>
          </p>
          <div className="aqsi-card__actions">
            <Button
              variant="primary"
              icon={<Undo2 size={14} aria-hidden="true" />}
              disabled={!ready}
              onClick={() => {
                if (ready && amount !== null) actions.send(selected.id, amount)
              }}
            >
              Вернуть деньги на карту
            </Button>
          </div>
        </div>
      )}

      <div className="aqsi-card__actions">
        <Button variant="ghost" onClick={actions.close}>
          Закрыть
        </Button>
      </div>
    </div>
  )
}
